import React, { useCallback, useContext, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import DragVerticalIcon from 'mdi-react/DragVerticalIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import MinusIcon from 'mdi-react/MinusIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import LockOutlineIcon from 'mdi-react/LockOutlineIcon';
import useConfig from '../../../util/useConfig';
import Api from '../../../util/api';
import { EventContext } from '../context';
import { partial } from '../../../util/translation/translation';
import { getQuestionType } from './QuestionnaireBuilder/QuestionType';

// The answer types an admin can pick, with a short hint for each
const ANSWER_TYPES = [
  { value: 'text_field', hint: 'typeHintText' },
  { value: 'radio_button', hint: 'typeHintSingle' },
  { value: 'checkbox', hint: 'typeHintMultiple' },
  { value: 'dropdown', hint: 'typeHintDropdown' },
  { value: 'reservation', hint: 'typeHintReservation' },
];
const CHOICE_TYPES = ['radio_button', 'checkbox', 'dropdown'];

const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const createSpot = () => ({
  id: newId(),
  title: '',
  subtitle: '',
  capacity: 1,
});

// Choices get a local key so rows keep their identity while reordering
const toChoiceRows = (choices = []) =>
  choices.map((c) => ({ key: newId(), choice: c.choice ?? '' }));

const reorder = (list, from, to) => {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

// The create/edit question form shown in QuestionModal. Creating saves the
// question to the library and this event's form; editing updates either
// the event's copy (type 'domain') or the library question.
const FormBuilder = ({ setShowModal, editableQuestion }) => {
  const isEditMode = !!editableQuestion;
  const qT = partial('question');
  const s = partial('shared');
  const original = editableQuestion?.question || {};

  const [text, setText] = useState(original.question || '');
  const [type, setType] = useState(original.question_type || null);
  const [choices, setChoices] = useState(() =>
    original.choices?.length
      ? toChoiceRows(original.choices)
      : [{ key: newId(), choice: '' }]
  );
  const [spots, setSpots] = useState(() =>
    original.options?.length ? original.options : [createSpot()]
  );
  const [submitted, setSubmitted] = useState(false);

  const { id } = useConfig();
  const {
    questions,
    pickedQuestions,
    setPickedQuestions,
    eventId,
    fetchQuestions,
    pickedQuestionId,
  } = useContext(EventContext);

  const createSharedQuestion = useCallback(async (sender) => {
    try {
      const foundSharedQuestion = questions.find(
        (q) =>
          (q.question?.question).toLowerCase() ===
          sender.data.question[0].question.toLowerCase()
      );
      if (foundSharedQuestion) {
        toast.error(qT('simillarQuestionsAvailableInLibrary'));
      } else {
        await Api.questions.createSharedQuestion(
          {
            domain: 'event',
            question: sender.data.question[0],
            domainId: eventId,
          },
          id
        );
        const { data } = await Api.questions.fetchDomainSpecificQuestions(
          eventId,
          id
        );
        setPickedQuestions(data.question);
        fetchQuestions(true);
        toast.success(qT('questionsAdded'));
      }
    } catch (error) {
      toast.error(error);
    }
  }, []);

  const updateQuestions = useCallback(
    async (sender, questionType = 'shared') => {
      const updateQuestion = sender.data.question[0];
      try {
        if (questionType === 'domain') {
          const currentPickedQuestions = [...pickedQuestions];
          const index = currentPickedQuestions.findIndex(
            (question) => question.id === editableQuestion.id
          );
          if (index !== -1) {
            currentPickedQuestions[index] = updateQuestion;
          }
          // The event's saved form; looked up if this page doesn't know it
          let formId = pickedQuestionId;
          if (!formId) {
            const { data: savedForm } =
              await Api.questions.fetchDomainSpecificQuestions(eventId, id);
            formId = savedForm?.id;
          }
          if (!formId) {
            throw qT('pleaseSaveTheQuestionnaireToEditThisQuestion');
          }
          await Api.questions.updateQuestion(
            formId,
            {
              type: questionType,
              question: currentPickedQuestions,
            },
            id
          );
          const { data } = await Api.questions.fetchDomainSpecificQuestions(
            eventId,
            id
          );
          setPickedQuestions(data.question);
        } else {
          await Api.questions.updateQuestion(
            editableQuestion.id,
            {
              type: questionType,
              question: updateQuestion,
              domain: 'event',
            },
            id
          );
          fetchQuestions(true);
        }
        toast.success(qT('questionsUpdated'));
      } catch (error) {
        toast.error(error);
      }
    },
    []
  );

  const isChoice = CHOICE_TYPES.includes(type);
  const isReservation = type === 'reservation';
  const filledChoices = choices.filter((c) => c.choice.trim());

  const errors = {
    text: !text.trim() && qT('errorQuestionRequired'),
    type: !type && qT('errorTypeRequired'),
    choices: isChoice && !filledChoices.length && qT('errorOptionsRequired'),
    spots:
      isReservation &&
      (!spots.length || spots.some((spot) => !spot.title.trim())) &&
      qT('errorSpotsRequired'),
  };
  const hasErrors = Object.values(errors).some(Boolean);
  const showError = (key) => submitted && errors[key];

  const handleSubmit = (evt) => {
    evt.preventDefault();
    setSubmitted(true);
    if (hasErrors) return;

    // Keep everything else on the question (id, required, askFor, ...)
    const questionData = {
      ...original,
      question: text.trim(),
      question_type: type,
    };
    if (isReservation) {
      questionData.options = spots.map((spot) => ({
        ...spot,
        title: spot.title.trim(),
        subtitle: spot.subtitle?.trim() || '',
      }));
      delete questionData.choices;
    } else if (isChoice) {
      questionData.choices = filledChoices.map((c) => ({
        choice: c.choice.trim(),
      }));
      delete questionData.options;
    } else {
      delete questionData.choices;
      delete questionData.options;
    }

    setShowModal();
    const wrappedSender = { data: { question: [questionData] } };
    if (isEditMode) {
      updateQuestions(wrappedSender, editableQuestion?.type);
    } else {
      createSharedQuestion(wrappedSender);
    }
  };

  const updateChoice = (key, value) =>
    setChoices((rows) =>
      rows.map((row) => (row.key === key ? { ...row, choice: value } : row))
    );

  const updateSpot = (index, field, value) =>
    setSpots((rows) =>
      rows.map((spot, i) => {
        if (i !== index) return spot;
        if (field === 'capacity') {
          const num = parseInt(value, 10);
          return { ...spot, capacity: Number.isNaN(num) || num < 1 ? 1 : num };
        }
        return { ...spot, [field]: value };
      })
    );

  const handleDragEnd = ({ source, destination }) => {
    if (!destination || destination.index === source.index) return;
    if (source.droppableId === 'choices') {
      setChoices((rows) => reorder(rows, source.index, destination.index));
    } else {
      setSpots((rows) => reorder(rows, source.index, destination.index));
    }
  };

  const lockedType = isEditMode && getQuestionType(type);
  const LockedIcon = lockedType?.icon;

  const renderTypePicker = () =>
    lockedType ? (
      <div className="qm-locked-type">
        <span className="qb-chip">
          {LockedIcon && <LockedIcon />}
          {lockedType.label}
        </span>
        <span className="qm-locked-type__note">
          <LockOutlineIcon size={14} />
          {qT('typeLocked')}
        </span>
      </div>
    ) : (
      <div className="qm-types" role="radiogroup" aria-label={qT('type')}>
        {ANSWER_TYPES.map(({ value, hint }) => {
          const meta = getQuestionType(value);
          const Icon = meta?.icon;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={type === value}
              className={`qm-type${type === value ? ' qm-type--on' : ''}`}
              onClick={() => setType(value)}
            >
              {Icon && <Icon />}
              <span className="qm-type__label">{meta?.label}</span>
              <span className="qm-type__hint">{qT(hint)}</span>
            </button>
          );
        })}
      </div>
    );

  const renderChoices = () => (
    <div className="qm-section">
      <div className="qm-section__head">
        <span className="qm-label">{qT('options')}</span>
        <span className="qm-muted">{qT('dragToReorder')}</span>
      </div>
      <Droppable droppableId="choices">
        {(provided) => (
          <div
            className="qm-rows"
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {choices.map((row, index) => (
              <Draggable key={row.key} draggableId={row.key} index={index}>
                {(dragProvided, snapshot) => (
                  <div
                    ref={dragProvided.innerRef}
                    {...dragProvided.draggableProps}
                    className={`qm-choice${
                      snapshot.isDragging ? ' qm-choice--dragging' : ''
                    }`}
                  >
                    <span
                      className="qm-grip"
                      {...dragProvided.dragHandleProps}
                      aria-label={qT('dragToReorder')}
                    >
                      <DragVerticalIcon size={18} />
                    </span>
                    <span
                      className={`qm-choice__marker qm-choice__marker--${type}`}
                      aria-hidden
                    />
                    <input
                      className="form-control"
                      value={row.choice}
                      placeholder={qT('optionNumber', { number: index + 1 })}
                      aria-label={qT('optionNumber', { number: index + 1 })}
                      onChange={(e) => updateChoice(row.key, e.target.value)}
                    />
                    <button
                      type="button"
                      className="square-icon-btn"
                      aria-label={qT('removeOption')}
                      disabled={choices.length === 1}
                      onClick={() =>
                        setChoices((rows) =>
                          rows.filter((r) => r.key !== row.key)
                        )
                      }
                    >
                      <CloseIcon />
                    </button>
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      {showError('choices') && (
        <span className="qm-error">{errors.choices}</span>
      )}
      <button
        type="button"
        className="qm-add-row"
        onClick={() =>
          setChoices((rows) => [...rows, { key: newId(), choice: '' }])
        }
      >
        <PlusIcon size={16} />
        {qT('reservationOptionAdd')}
      </button>
    </div>
  );

  const renderSpots = () => (
    <div className="qm-section">
      <div className="qm-section__head">
        <span className="qm-label">{qT('spots')}</span>
        <span className="qm-muted">{qT('spotsHint')}</span>
      </div>
      <Droppable droppableId="spots">
        {(provided) => (
          <div
            className="qm-rows"
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {spots.map((spot, index) => {
              const booked = spot.booked ?? 0;
              const full = booked >= spot.capacity;
              return (
                <Draggable
                  key={spot.id}
                  draggableId={String(spot.id)}
                  index={index}
                >
                  {(dragProvided, snapshot) => (
                    <div
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      className={`qm-spot${
                        snapshot.isDragging ? ' qm-spot--dragging' : ''
                      }`}
                    >
                      <div className="qm-spot__main">
                        <span
                          className="qm-grip"
                          {...dragProvided.dragHandleProps}
                          aria-label={qT('dragToReorder')}
                        >
                          <DragVerticalIcon size={18} />
                        </span>
                        <label className="qm-spot__field">
                          <span>{qT('reservationOptionTitle')} *</span>
                          <input
                            className="form-control"
                            value={spot.title}
                            onChange={(e) =>
                              updateSpot(index, 'title', e.target.value)
                            }
                          />
                        </label>
                        <label className="qm-spot__field">
                          <span>{qT('reservationOptionCapacity')} *</span>
                          <span className="qm-stepper">
                            <button
                              type="button"
                              aria-label={qT('fewer')}
                              disabled={spot.capacity <= 1}
                              onClick={() =>
                                updateSpot(index, 'capacity', spot.capacity - 1)
                              }
                            >
                              <MinusIcon size={16} />
                            </button>
                            <input
                              type="number"
                              min="1"
                              value={spot.capacity}
                              onChange={(e) =>
                                updateSpot(index, 'capacity', e.target.value)
                              }
                            />
                            <button
                              type="button"
                              aria-label={qT('more')}
                              onClick={() =>
                                updateSpot(index, 'capacity', spot.capacity + 1)
                              }
                            >
                              <PlusIcon size={16} />
                            </button>
                          </span>
                        </label>
                        <button
                          type="button"
                          className="square-icon-btn square-icon-btn--danger qm-spot__remove"
                          aria-label={qT('removeSpot')}
                          disabled={spots.length === 1}
                          onClick={() =>
                            setSpots((rows) =>
                              rows.filter((_, i) => i !== index)
                            )
                          }
                        >
                          <DeleteOutlineIcon />
                        </button>
                      </div>
                      <div className="qm-spot__subtitle">
                        <input
                          className="form-control"
                          value={spot.subtitle || ''}
                          aria-label={qT('reservationOptionSubtitle')}
                          placeholder={qT('spotSubtitlePlaceholder')}
                          onChange={(e) =>
                            updateSpot(index, 'subtitle', e.target.value)
                          }
                        />
                      </div>
                      {/* Bookings are only known for a saved question */}
                      {spot.booked !== undefined && (
                        <div className="qm-spot__status">
                          <div className="mini-bar">
                            <div
                              style={{
                                width: `${Math.min(
                                  100,
                                  (booked / spot.capacity) * 100
                                )}%`,
                                background: full ? '#b45309' : undefined,
                              }}
                            />
                          </div>
                          <span className={full ? 'qm-spot__full' : ''}>
                            {full
                              ? qT('spotFull', {
                                  booked,
                                  capacity: spot.capacity,
                                })
                              : qT('spotBooked', {
                                  booked,
                                  capacity: spot.capacity,
                                })}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      {showError('spots') && <span className="qm-error">{errors.spots}</span>}
      <button
        type="button"
        className="qm-add-row"
        onClick={() => setSpots((rows) => [...rows, createSpot()])}
      >
        <PlusIcon size={16} />
        {qT('addSpot')}
      </button>
    </div>
  );

  return (
    <form className="qm-form" onSubmit={handleSubmit} noValidate>
      <div className="qm-body">
        <div className="qm-section">
          <label className="qm-label" htmlFor="qm-question">
            {qT('question')}
          </label>
          <input
            id="qm-question"
            className={`form-control${showError('text') ? ' is-invalid' : ''}`}
            value={text}
            placeholder={qT('enterQuestion')}
            onChange={(e) => setText(e.target.value)}
            autoFocus
          />
          {showError('text') && <span className="qm-error">{errors.text}</span>}
        </div>

        <div className="qm-section">
          <span className="qm-label">{qT('answerType')}</span>
          {renderTypePicker()}
          {showError('type') && <span className="qm-error">{errors.type}</span>}
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          {isChoice && renderChoices()}
          {isReservation && renderSpots()}
        </DragDropContext>
      </div>

      <div className="qm-footer">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm mb-0"
          onClick={setShowModal}
        >
          {qT('cancel')}
        </button>
        <button type="submit" className="btn btn-primary btn-sm mb-0">
          {isEditMode ? s('save') : qT('add')}
        </button>
      </div>
    </form>
  );
};

export default FormBuilder;
